/**
 * SuperAdmin — Directory Moderation (Task 10.SA4)
 * Web only — ADR-014.
 *
 * Lists all centres with directory fields.
 * SuperAdmin toggles directory.isListed per centre — controls find.lifechiai.com visibility.
 * SuperAdmin changes tenant plan — PATCH /api/superadmin/tenants/:id/plan (Task 10.SA2)
 *
 * GET  /api/superadmin/directory          ?filter=all|listed|unlisted
 * PUT  /api/superadmin/directory/:id/listing  { isListed: Boolean }
 * PATCH /api/superadmin/tenants/:id/plan   { plan: String }
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FiGlobe, FiEye, FiEyeOff, FiRefreshCw, FiLoader, FiCheck, FiXCircle } from 'react-icons/fi';
import { getDirectoryCentres, toggleDirectoryListing, getDirectoryModerationQueue, moderateDirectoryTenant } from '../../utils/api';
import PlanChangeModal from './PlanChangeModal';

const PLAN_COLORS = {
  FREE: '#888', STARTER: '#2980b9', AI_ADMIN: '#8e44ad', SCALE: '#e67e22',
};
const FILTERS = [
  { key: 'all',      label: 'All' },
  { key: 'listed',   label: 'Listed' },
  { key: 'unlisted', label: 'Unlisted' },
  { key: 'pending',  label: 'Pending Review' },
];

const STYLE_ID = 'sa-dir-responsive';
if (!document.getElementById(STYLE_ID)) {
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    @keyframes sa-dir-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @media (max-width: 768px) {
      .sa-dir-table th:nth-child(3),
      .sa-dir-table td:nth-child(3) { display: none; }
    }
  `;
  document.head.appendChild(el);
}

const SpinnerView = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', gap: '10px', color: '#888' }}>
    <FiLoader style={{ fontSize: '18px', animation: 'sa-dir-spin 1s linear infinite' }} />
    <span style={{ fontSize: '13px' }}>Loading…</span>
  </div>
);

const RetryView = ({ message, onRetry }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px', gap: '12px', textAlign: 'center' }}>
    <div style={{ fontSize: '12px', color: '#e74c3c', maxWidth: '280px', lineHeight: '1.5' }}>{message}</div>
    <button type="button" onClick={onRetry}
      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '7px', border: 'none', background: '#4CE3CD', color: '#0D0F1F', fontSize: '12px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}>
      <FiRefreshCw style={{ fontSize: '12px' }} /> Retry
    </button>
  </div>
);

const DirectoryModeration = () => {
  const [centres,   setCentres]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [filter,    setFilter]    = useState('all');
  const [counts,    setCounts]    = useState({ total: 0, listed: 0, unlisted: 0 });
  const [confirm,   setConfirm]   = useState(null);    // { tenantId, tenantName, isListed }
  const [toggling,  setToggling]  = useState(null);    // tenantId currently toggling
  const [rowError,  setRowError]  = useState(null);    // { tenantId, message }
  const [planModal, setPlanModal] = useState(null);    // centre object | null
  const [approvingIds,    setApprovingIds]    = useState(new Set());
  const [rejectingIds,    setRejectingIds]    = useState(new Set());
  const [approveConfirm,  setApproveConfirm]  = useState(null);  // { tenantId, tenantName }
  const [rejectModal,     setRejectModal]      = useState(null);  // { tenantId, tenantName }
  const [rejectNote,      setRejectNote]       = useState('');
  const [rejectNoteError, setRejectNoteError]  = useState(null);

  const fetchCentres = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (filter === 'pending') {
        const res  = await getDirectoryModerationQueue();
        const list = res.data.data || [];
        setCentres(list);
        setCounts({ total: list.length, listed: 0, unlisted: 0 });
      } else {
        const res = await getDirectoryCentres(filter);
        const { tenants, total, listed, unlisted } = res.data.data;
        setCentres(tenants || []);
        setCounts({ total: total || 0, listed: listed || 0, unlisted: unlisted || 0 });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load centres.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchCentres(); }, [fetchCentres]);

  const handleToggle = useCallback(async () => {
    if (!confirm) return;
    const { tenantId, isListed } = confirm;
    setConfirm(null);
    setToggling(tenantId);
    setRowError(null);
    try {
      await toggleDirectoryListing(tenantId, isListed);
      setCentres(prev => prev.map(c =>
        String(c._id) === tenantId
          ? { ...c, directory: { ...c.directory, isListed } }
          : c
      ));
      setCounts(prev => ({
        ...prev,
        listed:   isListed ? prev.listed + 1 : prev.listed - 1,
        unlisted: isListed ? prev.unlisted - 1 : prev.unlisted + 1,
      }));
    } catch (err) {
      setRowError({ tenantId, message: err.response?.data?.message || 'Toggle failed.' });
    } finally {
      setToggling(null);
    }
  }, [confirm]);

  const handleApproveConfirm = useCallback(async () => {
    if (!approveConfirm) return;
    const { tenantId } = approveConfirm;
    setApproveConfirm(null);
    setApprovingIds(prev => new Set(prev).add(tenantId));
    try {
      await moderateDirectoryTenant(tenantId, 'approve', '');
      await fetchCentres();
    } catch (err) {
      setRowError({ tenantId, message: err.response?.data?.message || 'Approve failed.' });
      setApprovingIds(prev => { const s = new Set(prev); s.delete(tenantId); return s; });
    }
  }, [approveConfirm, fetchCentres]);

  const handleRejectConfirm = useCallback(async () => {
    if (!rejectModal) return;
    const note = rejectNote.trim();
    if (!note) { setRejectNoteError('Moderation note is required for rejection'); return; }
    const { tenantId } = rejectModal;
    setRejectModal(null);
    setRejectNote('');
    setRejectNoteError(null);
    setRejectingIds(prev => new Set(prev).add(tenantId));
    try {
      await moderateDirectoryTenant(tenantId, 'reject', note);
      await fetchCentres();
    } catch (err) {
      setRowError({ tenantId, message: err.response?.data?.message || 'Reject failed.' });
      setRejectingIds(prev => { const s = new Set(prev); s.delete(tenantId); return s; });
    }
  }, [rejectModal, rejectNote, fetchCentres]);

  return (
    <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <FiGlobe style={{ fontSize: '16px', color: '#4CE3CD' }} />
        <span style={{ fontSize: '12px', fontWeight: '700', color: '#1a1a2e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Directory Moderation</span>
        {!loading && !error && (
          <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#aaa' }}>
            {filter === 'pending'
              ? `${centres.length} pending review`
              : `${counts.listed} listed · ${counts.unlisted} unlisted · ${counts.total} total`}
          </span>
        )}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0' }}>
        {FILTERS.map(f => (
          <button key={f.key} type="button" onClick={() => setFilter(f.key)}
            style={{ padding: '10px 20px', border: 'none', background: 'none', fontSize: '12px', fontWeight: '700', color: filter === f.key ? '#4CE3CD' : '#999', borderBottom: filter === f.key ? '2px solid #4CE3CD' : '2px solid transparent', cursor: 'pointer', minHeight: '44px', transition: 'color 0.15s' }}>
            {f.label}
          </button>
        ))}
      </div>

      {loading && <SpinnerView />}
      {!loading && error   && <RetryView message={error} onRetry={fetchCentres} />}
      {!loading && !error  && centres.length === 0 && (
        <div style={{ padding: '48px', textAlign: 'center', color: '#bbb', fontSize: '13px' }}>
          {filter === 'pending' ? 'No centres pending review.' : 'No centres found.'}
        </div>
      )}

      {!loading && !error && centres.length > 0 && filter !== 'pending' && (
        <div style={{ overflowX: 'auto' }}>
          <table className="sa-dir-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={TH}>Centre</th>
                <th style={TH}>Plan</th>
                <th style={TH}>Type</th>
                <th style={TH}>Status</th>
                <th style={{ ...TH, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {centres.map(c => {
                const isListed  = c.directory?.isListed || false;
                const isLoading = toggling === String(c._id);
                const err       = rowError?.tenantId === String(c._id) ? rowError.message : null;
                return (
                  <tr key={c._id} style={{ borderBottom: '1px solid #f8f8f8' }}>
                    <td style={TD}>
                      <div style={{ fontWeight: '600', color: '#1a1a2e' }}>{c.tenantName}</div>
                      {c.city && <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>{c.city}</div>}
                      {err   && <div style={{ fontSize: '11px', color: '#e74c3c', marginTop: '3px' }}>{err}</div>}
                    </td>
                    <td style={TD}>
                      <span style={{ fontSize: '10px', fontWeight: '700', borderRadius: '4px', padding: '2px 6px', background: `${PLAN_COLORS[c.plan] || '#888'}18`, color: PLAN_COLORS[c.plan] || '#888' }}>
                        {c.plan || '—'}
                      </span>
                    </td>
                    <td style={TD}>
                      <span style={{ fontSize: '11px', color: '#888' }}>{c.tenantType || 'centre'}</span>
                    </td>
                    <td style={TD}>
                      <span style={{ fontSize: '10px', fontWeight: '700', borderRadius: '4px', padding: '2px 6px', background: c.isActive ? 'rgba(39,174,96,0.10)' : 'rgba(150,150,150,0.12)', color: c.isActive ? '#27ae60' : '#999' }}>
                        {c.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ ...TD, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button type="button"
                          onClick={() => setPlanModal(c)}
                          style={{ padding: '7px 12px', borderRadius: '6px', border: '1px solid #e0e0e0', background: '#fff', color: '#555', fontSize: '12px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' }}>
                          Change Plan
                        </button>
                        <button type="button" disabled={isLoading}
                          onClick={() => setConfirm({ tenantId: String(c._id), tenantName: c.tenantName, isListed: !isListed })}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '6px', border: `1px solid ${isListed ? '#e74c3c' : '#4CE3CD'}`, background: isListed ? 'rgba(231,76,60,0.06)' : 'rgba(76,227,205,0.08)', color: isListed ? '#e74c3c' : '#4CE3CD', fontSize: '12px', fontWeight: '700', cursor: isLoading ? 'not-allowed' : 'pointer', minHeight: '44px', opacity: isLoading ? 0.5 : 1 }}>
                          {isLoading
                            ? <FiLoader style={{ animation: 'sa-dir-spin 1s linear infinite' }} />
                            : isListed ? <FiEyeOff /> : <FiEye />}
                          {isLoading ? '…' : isListed ? 'Unlist' : 'List'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && centres.length > 0 && filter === 'pending' && (
        <div style={{ overflowX: 'auto' }}>
          <table className="sa-dir-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={TH}>Centre</th>
                <th style={TH}>Plan</th>
                <th style={TH}>Submitted</th>
                <th style={{ ...TH, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {centres.map(c => {
                const isApproving  = approvingIds.has(String(c._id));
                const isRejecting  = rejectingIds.has(String(c._id));
                const isSubmitting = isApproving || isRejecting;
                const err          = rowError?.tenantId === String(c._id) ? rowError.message : null;
                return (
                  <tr key={c._id} style={{ borderBottom: '1px solid #f8f8f8' }}>
                    <td style={TD}>
                      <div style={{ fontWeight: '600', color: '#1a1a2e' }}>{c.tenantName}</div>
                      {c.city && <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>{c.city}</div>}
                      {err   && <div style={{ fontSize: '11px', color: '#e74c3c', marginTop: '3px' }}>{err}</div>}
                    </td>
                    <td style={TD}>
                      <span style={{ fontSize: '10px', fontWeight: '700', borderRadius: '4px', padding: '2px 6px', background: `${PLAN_COLORS[c.plan] || '#888'}18`, color: PLAN_COLORS[c.plan] || '#888' }}>
                        {c.plan || '—'}
                      </span>
                    </td>
                    <td style={TD}>
                      <span style={{ fontSize: '12px', color: '#888' }}>
                        {c.directory?.submittedAt
                          ? new Date(c.directory.submittedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                          : '—'}
                      </span>
                    </td>
                    <td style={{ ...TD, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button type="button" disabled={isSubmitting}
                          onClick={() => !isSubmitting && setApproveConfirm({ tenantId: String(c._id), tenantName: c.tenantName })}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '6px', border: '1px solid #4CE3CD', background: 'rgba(76,227,205,0.08)', color: '#4CE3CD', fontSize: '12px', fontWeight: '700', cursor: isSubmitting ? 'not-allowed' : 'pointer', minHeight: '44px', opacity: isSubmitting ? 0.5 : 1 }}>
                          {isApproving ? <FiLoader style={{ animation: 'sa-dir-spin 1s linear infinite' }} /> : <FiCheck />}
                          {isApproving ? '…' : 'Approve'}
                        </button>
                        <button type="button" disabled={isSubmitting}
                          onClick={() => { if (!isSubmitting) { setRejectModal({ tenantId: String(c._id), tenantName: c.tenantName }); setRejectNote(''); setRejectNoteError(null); } }}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '6px', border: '1px solid #e74c3c', background: 'rgba(231,76,60,0.06)', color: '#e74c3c', fontSize: '12px', fontWeight: '700', cursor: isSubmitting ? 'not-allowed' : 'pointer', minHeight: '44px', opacity: isSubmitting ? 0.5 : 1 }}>
                          {isRejecting ? <FiLoader style={{ animation: 'sa-dir-spin 1s linear infinite' }} /> : <FiXCircle />}
                          {isRejecting ? '…' : 'Reject'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Directory listing ConfirmModal */}
      {confirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px 28px 24px', maxWidth: '360px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' }}>Confirm</div>
            <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', marginBottom: '24px' }}>
              {confirm.isListed
                ? <span>Add <strong>{confirm.tenantName}</strong> to the public directory?</span>
                : <span>Remove <strong>{confirm.tenantName}</strong> from the public directory?</span>}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConfirm(null)}
                style={{ padding: '9px 18px', borderRadius: '7px', border: '1px solid #e0e0e0', background: '#fff', color: '#555', fontSize: '13px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' }}>
                Cancel
              </button>
              <button type="button" onClick={handleToggle}
                style={{ padding: '9px 18px', borderRadius: '7px', border: 'none', background: confirm.isListed ? '#4CE3CD' : '#e74c3c', color: confirm.isListed ? '#0D0F1F' : '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}>
                {confirm.isListed ? 'List' : 'Unlist'}
              </button>
            </div>
          </div>
        </div>
      )}

      {approveConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px 28px 24px', maxWidth: '360px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' }}>Approve Listing?</div>
            <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', marginBottom: '24px' }}>
              Approve <strong>{approveConfirm.tenantName}</strong> for the public directory?
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setApproveConfirm(null)}
                style={{ padding: '9px 18px', borderRadius: '7px', border: '1px solid #e0e0e0', background: '#fff', color: '#555', fontSize: '13px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' }}>
                Cancel
              </button>
              <button type="button" onClick={handleApproveConfirm}
                style={{ padding: '9px 18px', borderRadius: '7px', border: 'none', background: '#4CE3CD', color: '#0D0F1F', fontSize: '13px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}>
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px 28px 24px', maxWidth: '400px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' }}>Reject Listing</div>
            <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.5', marginBottom: '14px' }}>
              Reject <strong>{rejectModal.tenantName}</strong> from the directory. Reason:
            </div>
            <textarea
              value={rejectNote}
              onChange={e => { setRejectNote(e.target.value); setRejectNoteError(null); }}
              placeholder="Reason for rejection (required)"
              rows={3}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: `1px solid ${rejectNoteError ? '#e74c3c' : '#e0e0e0'}`, fontSize: '13px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
            {rejectNoteError && <div style={{ fontSize: '11px', color: '#e74c3c', marginTop: '4px' }}>{rejectNoteError}</div>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button type="button" onClick={() => { setRejectModal(null); setRejectNote(''); setRejectNoteError(null); }}
                style={{ padding: '9px 18px', borderRadius: '7px', border: '1px solid #e0e0e0', background: '#fff', color: '#555', fontSize: '13px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' }}>
                Cancel
              </button>
              <button type="button" onClick={handleRejectConfirm}
                style={{ padding: '9px 18px', borderRadius: '7px', border: 'none', background: '#e74c3c', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}>
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {planModal && (
        <PlanChangeModal
          tenant={planModal}
          onClose={() => setPlanModal(null)}
          onChanged={() => { setPlanModal(null); fetchCentres(); }}
        />
      )}
    </div>
  );
};

const TH = { padding: '10px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap' };
const TD = { padding: '12px 16px', verticalAlign: 'middle' };

export default DirectoryModeration;
